import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  IconTarget,
  IconShieldCheck,
  IconClock,
  IconTrendingUp,
} from "@tabler/icons-react";
import SampleDocumentFan from "../components/ui/SampleDocumentFan";
import {
  FileText,
  ReceiptText,
  ScrollText,
  Truck,
  ClipboardCheck,
  Calculator,
  ScanText,
  Camera,
  ShieldCheck,
  Clock3,
  Wallet,
  BadgeCheck,
  Sparkles,
  Zap,
  ArrowRight,
  Briefcase,
  Package,
  Star,
} from "lucide-react";
import { RESUME_DISPLAY_NAME } from "../lib/docTypeDisplayNames";
import { resolveThumbnailUrl } from "../lib/publicApi";
import StoreNav from "../components/store/StoreNav";
import PublicSiteFooter from "../components/store/PublicSiteFooter";
import { useAuth } from "../context/AuthContext";

const BRAND_ORANGE = "#F47518";
const BRAND_NAVY = "#1A3C6E";
const HERO_HEADLINE_SIZE = "text-[2.4rem] sm:text-5xl lg:text-[3.35rem]";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const createDocs = [
  {
    title: "Business Proposal Builder",
    subtitle: "Win funding with bank-ready, investor-ready & grant-ready proposals built around your numbers",
    desc: "Create Business Plans, Business Grant Proposals, NGO Grant Proposals, and Service Proposals faster, with help generating SWOT analysis, financials, market analysis, executive summaries, and more.",
    icon: Briefcase,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    border: "border-emerald-100",
    leftBorder: "border-l-emerald-500",
    path: "/services/business-proposal-builder",
  },
  {
    title: "Invoice",
    subtitle: "Create custom invoices by uploading your logo and company details to professional templates",
    desc: "Generate detailed invoices with line items, totals, and your business branding.",
    icon: ReceiptText,
    color: "text-orange-600",
    bg: "bg-orange-50",
    border: "border-orange-100",
    leftBorder: "border-l-orange-400",
    path: "/services/invoice-generator",
  },
  {
    title: "Quotation",
    subtitle: "Send branded quotations with your logo and company details instantly",
    desc: "Draft professional price quotations with validity periods and terms.",
    icon: Calculator,
    color: "text-violet-600",
    bg: "bg-violet-50",
    border: "border-violet-100",
    leftBorder: "border-l-violet-500",
    path: "/services/quotation-generator",
  },
  {
    title: "Purchase Order Builder",
    subtitle: "Generate procurement-ready POs with fully itemized details for any supplier or vendor",
    desc: "Issue itemised purchase orders with auto-totals, VAT, amount-in-words, and expert-suggested supply notes.",
    icon: Package,
    color: "text-sky-600",
    bg: "bg-sky-50",
    border: "border-sky-100",
    leftBorder: "border-l-sky-500",
    path: "/services/purchase-order-builder",
  },
  {
    title: RESUME_DISPLAY_NAME,
    subtitle: "Build a recruiter-ready resume and matching cover letter tailored to your industry that get you noticed and interviewed",
    desc: "Build a professional resume and personalized cover letter with smart summaries and tailored skills.",
    icon: FileText,
    color: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-100",
    leftBorder: "border-l-blue-600",
    path: "/services/resume-builder",
  },
  {
    title: "Proforma Invoice",
    subtitle: "Create custom proforma invoices by uploading your logo and company details to professional pre-sale templates",
    desc: "Create formal quotation-style invoices for pre-sale agreements.",
    icon: ScrollText,
    color: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-100",
    leftBorder: "border-l-amber-500",
    path: "/services/proforma-invoice",
  },
  {
    title: "Delivery Note",
    subtitle: "Issue professional proof-of-delivery documents that keep your logistics airtight",
    desc: "Issue delivery confirmation documents with item lists and signatures.",
    icon: ClipboardCheck,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
    border: "border-indigo-100",
    leftBorder: "border-l-indigo-500",
    path: "/services/delivery-note",
  },
  {
    title: "Waybill",
    subtitle: "Generate shipment tracking documents that keep your logistics professional end to end",
    desc: "Prepare shipping waybills with origin, destination, and cargo details.",
    icon: Truck,
    color: "text-teal-600",
    bg: "bg-teal-50",
    border: "border-teal-100",
    leftBorder: "border-l-teal-500",
    path: "/services/waybill-generator",
  },
];

const guideVideos = [
  {
    title: "Business Plan Builder",
    videoId: "3dDb86ZyjcQ",
  },
  {
    title: "Business Grant Proposal Builder",
    videoId: "9zSM0fLhO0s",
  },
  {
    title: "NGO Grant Proposal Builder",
    videoId: "J6kS8Q8UY1I",
  },
  {
    title: "Service Proposal Builder",
    videoId: "RhPWOLGVnCo",
  },
  {
    title: "Quotation Generator",
    videoId: "lw2rAHxbsOw",
  },
  {
    title: "Resume & Cover Letter Builder",
    videoId: "7BJVTe6s1Vg",
  },
];

const heroUtilities = [
  {
    title: "PDF Compression",
    subtitle: "Reduce files for sharing, emailing & archiving using Standard, Target & Aggressive compression levels",
    icon: ScanText,
    color: "text-rose-600",
    bg: "bg-rose-50",
    border: "border-rose-100",
    leftBorder: "border-l-rose-500",
    path: "/services/pdf-compression",
  },
  {
    title: "Visa Photo",
    subtitle: "Generate passport-ready visa photos sized to any country's official requirements in seconds",
    icon: Camera,
    color: "text-teal-600",
    bg: "bg-teal-50",
    border: "border-teal-100",
    leftBorder: "border-l-teal-500",
    path: "/services/visa-passport-photo",
  },
];

const KEY_RATINGS = [
  {
    Icon: IconTarget,
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-600",
    heading: "Win More Opportunities",
    description: "Well-structured proposals increase your chances of approval.",
  },
  {
    Icon: IconShieldCheck,
    iconBg: "bg-blue-50",
    iconColor: "text-blue-600",
    heading: "Professional & Branded",
    description: "Create polished, branded documents that build trust and credibility.",
  },
  {
    Icon: IconClock,
    iconBg: "bg-purple-50",
    iconColor: "text-purple-600",
    heading: "Save Time",
    description: "Ready-to-use templates and smart suggestions help you create faster.",
  },
  {
    Icon: IconTrendingUp,
    iconBg: "bg-orange-50",
    iconColor: "text-orange-600",
    heading: "Data-Driven",
    description: "Include financials, charts and forecasts with professional layouts.",
  },
];

const whyCards = [
  {
    title: "Faster than Canva or Word",
    desc: "Skip layout fiddling. Pick a template, fill the form, download in under a minute.",
    icon: Zap,
  },
  {
    title: "Built for real-world use",
    desc: "Invoices clients pay, visa photos that get accepted, business workflows that ship.",
    icon: BadgeCheck,
  },
  {
    title: "No subscriptions",
    desc: "Pay only when you need a document. Credits never expire.",
    icon: Wallet,
  },
  {
    title: "Secure Processing",
    desc: "Documents processed securely and never stored beyond completion.",
    icon: ShieldCheck,
  },
  {
    title: "No Design Skills Needed",
    desc: "Create professional documents without formatting or layout stress.",
    icon: Sparkles,
  },
  {
    title: "Credits Never Expire",
    desc: "Buy when you need them. No expiry, no monthly fees.",
    icon: Clock3,
  },
];

function HeroDocumentRow({ item, onClick }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`service-stack-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
      className={`group w-full border-l-4 text-left rounded-2xl border ${item.leftBorder || ""} ${item.border} ${item.bg} px-4 py-3 shadow-sm cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400`}
    >
      <div className="flex w-full items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="shrink-0 rounded-xl bg-white/90 p-2 shadow-sm ring-1 ring-white/80">
            <Icon className={`h-5 w-5 ${item.color}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className={`text-[15px] font-bold uppercase leading-snug ${item.color}`}>
              {item.title}
            </div>
            <div className="text-xs leading-relaxed text-slate-500 sm:text-sm">
              {item.subtitle}
            </div>
          </div>
        </div>
        <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-slate-600" />
      </div>
    </button>
  );
}

function TemplateCard({ label, angle = "", top = "", z = "", img = "/template-preview.png" }) {
  return (
    <div
      className={`absolute w-[210px] rounded-2xl bg-white p-3 shadow-xl ring-1 ring-slate-200 ${angle} ${top} ${z}`}
    >
      <img
        src={img}
        alt={label}
        className="h-[248px] w-full rounded-xl object-cover object-top"
      />
      <div className="pt-3 text-center text-sm font-semibold text-slate-700">{label}</div>
    </div>
  );
}

function SectionHeading({ title, subtitle, compact = false }) {
  return (
    <div className={`${compact ? "mb-8" : "mb-10"} text-center`}>
      <h2 className="text-4xl font-extrabold tracking-tight text-slate-950 md:text-5xl">
        {title}
      </h2>
      <p className="mx-auto mt-3 max-w-[760px] text-lg leading-8 text-slate-600 md:text-[1.35rem] md:leading-9">
        {subtitle}
      </p>
    </div>
  );
}

export default function DocManiaLandingPage() {
  const [thumbs, setThumbs] = useState({});
  const navigate = useNavigate();
  const { user } = useAuth();
  const createDocumentsHref = user ? "/dashboard" : "/login?redirect=%2Fdashboard";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/templates/showcase`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const map = {};
        (data.templates || []).forEach((t) => {
          if (t.document_type && t.thumbnail_url) {
            map[t.document_type] = resolveThumbnailUrl(t.thumbnail_url);
          }
        });
        setThumbs(map);
      } catch (_) {
        /* silent */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="min-h-screen overflow-x-hidden bg-white text-slate-900">
      <StoreNav showContactOnDesktop />

      {/* HERO */}
      <section className="mx-auto max-w-[1400px] px-6 pt-6 pb-12 md:pt-8 md:pb-14">
        <div className="grid items-start gap-10 lg:grid-cols-12 lg:gap-8">
          {/* LEFT: hero content */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-1.5 text-sm font-medium text-[#F47518]">
              <Star className="h-3.5 w-3.5 fill-[#F47518] text-[#F47518]" />
              All-in-One Document Creation Platform
            </div>

            <h1 className={`mt-4 font-extrabold leading-[1.08] tracking-tight ${HERO_HEADLINE_SIZE}`}>
              <span className="block whitespace-nowrap text-black">
                Win Funding.
              </span>
              <span className="block whitespace-nowrap text-black">
                Close Clients.
              </span>
              <div className={`relative ${HERO_HEADLINE_SIZE}`}>
                <span className="invisible inline-block whitespace-nowrap font-extrabold tracking-tight">
                  Close{" "}
                </span>
                <div className="absolute left-0 top-0" style={{ marginTop: "4px", marginBottom: "4px" }}>
                  <span style={{ color: "#B8860B", fontSize: "1.1rem", fontWeight: "bold" }}>→</span>
                  <span
                    style={{
                      fontFamily: "'Dancing Script', cursive",
                      fontSize: "1.8rem",
                      color: "#F47518",
                      margin: "0 6px",
                    }}
                  >
                    with
                  </span>
                  <span style={{ color: "#B8860B", fontSize: "1.1rem", fontWeight: "bold" }}>←</span>
                </div>
              </div>
              <span className="mt-1 block font-extrabold" style={{ color: BRAND_ORANGE }}>
                Professional Documents,
              </span>
              <span className="block font-extrabold" style={{ color: BRAND_ORANGE }}>
                Generated in Minutes.
                <svg viewBox="0 0 120 12" width="120" height="12" style={{ display: "block", marginTop: "-4px" }}>
                  <path
                    d="M4,9 Q60,1 116,9"
                    stroke="#F47518"
                    strokeWidth="3"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </span>
            </h1>

            <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
              Create business plans, grant proposals, service proposals, invoices, quotations,
              resumes, purchase orders and more — without design or formatting stress.
            </p>

            <div className="mt-5 flex flex-wrap gap-3">
              <a
                href="/proposal"
                data-testid="hero-get-started-btn"
                className="inline-flex items-center gap-2 rounded-2xl px-6 py-3.5 text-base font-semibold text-white shadow-sm transition hover:opacity-95 sm:px-7 sm:py-4 sm:text-lg"
                style={{ backgroundColor: BRAND_ORANGE }}
              >
                Create Professional Documents
                <ArrowRight className="h-5 w-5" />
              </a>
              <a
                href="#templates"
                data-testid="hero-preview-templates-btn"
                className="inline-flex items-center rounded-2xl border border-slate-200 bg-white px-6 py-3.5 text-base font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 sm:px-7 sm:py-4 sm:text-lg"
              >
                See Live Templates
              </a>
            </div>

            <SampleDocumentFan />

          </div>

          {/* RIGHT: document type list */}
          <aside className="w-full lg:col-span-5">
            <div className="w-full lg:sticky lg:top-6">
              <div
                className="mb-3 text-xs font-bold uppercase tracking-[0.18em]"
                style={{ color: BRAND_NAVY }}
              >
                CREATE DOCUMENTS
              </div>
              <div className="flex w-full flex-col gap-2">
                {createDocs.map((item) => (
                  <HeroDocumentRow
                    key={item.title}
                    item={item}
                    onClick={() => navigate(item.path)}
                  />
                ))}
              </div>

              <div
                className="mb-3 mt-6 text-xs font-bold uppercase tracking-[0.18em]"
                style={{ color: BRAND_NAVY }}
              >
                TOOLS &amp; UTILITIES
              </div>
              <div className="flex w-full flex-col gap-2">
                {heroUtilities.map((item) => (
                  <HeroDocumentRow
                    key={item.title}
                    item={item}
                    onClick={() => navigate(item.path)}
                  />
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* GUIDE VIDEOS */}
      <section className="mx-auto max-w-[1200px] px-6 py-16 md:py-18">
        <SectionHeading
          title="See How Easy It Is"
          subtitle="Business plans, grant proposals, service proposals, quotations, and resumes — one quick video for each."
        />

        <div className="grid gap-6 md:grid-cols-2">
          {guideVideos.map((video) => (
            <div key={video.videoId} className="rounded-3xl overflow-hidden bg-gray-50 p-2">
              <div className="aspect-video w-full rounded-2xl overflow-hidden">
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${video.videoId}`}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <p className="mt-3 font-semibold text-gray-900">{video.title}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TEMPLATES (standalone) */}
      <section id="templates" className="mx-auto max-w-[1200px] px-6 py-16 md:py-18">
        <SectionHeading
          title="Real Templates. Instant Generation."
          subtitle="Choose from professionally designed templates and generate polished documents in seconds."
          compact
        />

        <div className="relative mx-auto mt-6 hidden h-[405px] max-w-[940px] md:block">
          <TemplateCard label={RESUME_DISPLAY_NAME} img={thumbs.resume || "/templates/resume-1.jpg"} angle="-rotate-6" top="top-12 left-6" z="z-10" />
          <TemplateCard label="Invoice" img={thumbs.invoice || "/templates/invoice-2.jpg"} angle="-rotate-2" top="top-0 left-52" z="z-20" />
          <TemplateCard label="Proforma Invoice" img={thumbs.proforma_invoice || "/templates/proforma-3.jpg"} angle="rotate-3" top="top-7 left-[24rem]" z="z-30" />
          <TemplateCard label="Waybill" img={thumbs.waybill || "/templates/waybill-4.jpg"} angle="-rotate-3" top="top-26 left-[10rem]" z="z-40" />
          <TemplateCard label="Delivery Note" img={thumbs.delivery_note || "/templates/delivery-2.jpg"} angle="rotate-4" top="top-34 left-[30rem]" z="z-20" />
          <TemplateCard label="Quotation" img={thumbs.quotation || "/templates/quotation-5.jpg"} angle="-rotate-1" top="top-20 left-[40rem]" z="z-10" />
        </div>

        {/* Mobile: simple horizontal-scroll thumbnail strip (no overflow on body) */}
        <div className="mt-6 grid grid-cols-2 gap-4 md:hidden">
          {[
            { label: RESUME_DISPLAY_NAME,          img: thumbs.resume || "/templates/resume-1.jpg" },
            { label: "Invoice",         img: thumbs.invoice || "/templates/invoice-2.jpg" },
            { label: "Proforma",        img: thumbs.proforma_invoice || "/templates/proforma-3.jpg" },
            { label: "Waybill",         img: thumbs.waybill || "/templates/waybill-4.jpg" },
            { label: "Delivery Note",   img: thumbs.delivery_note || "/templates/delivery-2.jpg" },
            { label: "Quotation",       img: thumbs.quotation || "/templates/quotation-5.jpg" },
          ].map((t) => (
            <div key={t.label} className="rounded-2xl bg-white p-2 shadow-md ring-1 ring-slate-200">
              <img src={t.img} alt={t.label} className="h-44 w-full rounded-xl object-cover object-top" />
              <div className="pt-2 text-center text-xs font-semibold text-slate-700">{t.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY DOCMANIA */}
      <section className="mx-auto max-w-[1200px] px-6 py-16 md:py-18">
        <SectionHeading
          title="Why Choose DocMania?"
          subtitle="A reliable, credit-based platform designed for professionals who value quality and efficiency."
        />

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {whyCards.map((card, idx) => {
            const Icon = card.icon;
            return (
              <div
                key={idx}
                className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm"
              >
                <div className="flex items-start gap-5">
                  <div className="rounded-2xl bg-orange-50 p-3 ring-1 ring-orange-100">
                    <Icon className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold tracking-tight text-slate-950">
                      {card.title}
                    </h3>
                    <p className="mt-3 text-base leading-8 text-slate-600">
                      {card.desc}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* HOW CREDITS WORK */}
      <section id="pricing" className="mx-auto max-w-[1200px] px-6 py-16 md:py-18">
        <SectionHeading
          title="Simple, Transparent Pricing"
          subtitle="No subscriptions — pay only when you download. Credits never expire."
        />

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5">
          <div
            data-testid="credit-pricing-business-proposal"
            className="rounded-3xl border border-emerald-100 bg-emerald-50 p-7 shadow-sm"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-100">
              <FileText className="h-6 w-6 text-emerald-600" />
            </div>
            <div className="text-3xl font-extrabold tracking-tight text-slate-950">
              20 credits
            </div>
            <p className="mt-1 text-sm font-semibold text-emerald-600">
              per Business Proposal
            </p>
            <p className="mt-3 text-[15px] leading-7 text-slate-600">
              Auto-assisted multi-page proposal with financials, projections,
              SWOT and grant utilization.
            </p>
          </div>

          <div
            data-testid="credit-pricing-purchase-order"
            className="rounded-3xl border border-sky-100 bg-sky-50 p-7 shadow-sm"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-100">
              <FileText className="h-6 w-6 text-sky-600" />
            </div>
            <div className="text-3xl font-extrabold tracking-tight text-slate-950">
              5 credits
            </div>
            <p className="mt-1 text-sm font-semibold text-sky-600">
              per Purchase Order
            </p>
            <p className="mt-3 text-[15px] leading-7 text-slate-600">
              Vendor-ready POs with itemised line totals, VAT, and your
              company logo on every page.
            </p>
          </div>

          <div
            data-testid="credit-pricing-document"
            className="rounded-3xl border border-blue-100 bg-blue-50 p-7 shadow-sm"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-100">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="text-3xl font-extrabold tracking-tight text-slate-950">
              3 credits
            </div>
            <p className="mt-1 text-sm font-semibold text-blue-600">
              per {RESUME_DISPLAY_NAME} or Document
            </p>
            <p className="mt-3 text-[15px] leading-7 text-slate-600">
              {RESUME_DISPLAY_NAME}, Invoice, Proforma, Waybill, Delivery Note, or Quotation —
              all priced the same.
            </p>
          </div>

          <div
            data-testid="credit-pricing-pdf"
            className="rounded-3xl border border-red-100 bg-red-50 p-7 shadow-sm"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-100">
              <ScanText className="h-6 w-6 text-red-600" />
            </div>
            <div className="text-3xl font-extrabold tracking-tight text-slate-950">
              2 credits
            </div>
            <p className="mt-1 text-sm font-semibold text-red-600">
              per PDF Compression
            </p>
            <p className="mt-3 text-[15px] leading-7 text-slate-600">
              Reduce file sizes by up to 80% while keeping documents
              readable and crisp.
            </p>
          </div>

          <div
            data-testid="credit-pricing-visa"
            className="rounded-3xl border border-purple-100 bg-purple-50 p-7 shadow-sm"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-100">
              <Camera className="h-6 w-6 text-purple-600" />
            </div>
            <div className="text-3xl font-extrabold tracking-tight text-slate-950">
              4 credits
            </div>
            <p className="mt-1 text-sm font-semibold text-purple-600">
              per Visa Photo
            </p>
            <p className="mt-3 text-[15px] leading-7 text-slate-600">
              Visa-compliant passport photos formatted to official country
              specifications.
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-[1200px] px-6 py-16 md:py-18">
        <div className="rounded-[32px] bg-slate-50 px-8 py-12 text-center ring-1 ring-slate-200 md:px-10 md:py-14">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
            <img
              src="/logo.png"
              alt="DocMania logo"
              className="h-12 w-12 object-contain"
            />
          </div>

          <h2 className="mt-5 text-4xl font-extrabold tracking-tight text-slate-950 md:text-5xl">
            Start creating professional documents in seconds.
          </h2>

          <p className="mx-auto mt-4 max-w-[700px] text-lg leading-8 text-slate-600 md:text-[1.35rem] md:leading-9">
            No subscriptions. No complexity. Just results.
          </p>

          <div className="mt-7">
            <a
              href="/signup"
              data-testid="final-cta-btn"
              className="inline-flex items-center gap-2 rounded-2xl bg-orange-500 px-7 py-4 text-lg font-semibold text-white shadow-sm transition hover:bg-orange-600"
            >
              Create Business Proposal Now
              <ArrowRight className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* KEY RATINGS */}
      <section className="border-t border-slate-200 bg-white py-12" data-testid="key-ratings-section">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {KEY_RATINGS.map((item) => {
              const Icon = item.Icon;
              return (
                <div key={item.heading} className="flex items-start gap-4">
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${item.iconBg}`}
                  >
                    <Icon size={24} stroke={1.75} className={item.iconColor} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">{item.heading}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <PublicSiteFooter />

      {/* MOBILE STICKY CTA (md:hidden) */}
      <div
        data-testid="mobile-sticky-cta"
        className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur md:hidden"
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      >
        <div className="flex gap-3">
          <Link
            to={createDocumentsHref}
            className="flex flex-1 items-center justify-center rounded-2xl bg-orange-500 px-3 py-3.5 text-center text-sm font-semibold leading-tight text-white shadow-md transition active:scale-[0.99] hover:bg-orange-600 sm:text-base"
            data-testid="mobile-sticky-create-documents"
          >
            Create Documents
          </Link>
          <Link
            to="/templates"
            className="flex flex-1 items-center justify-center rounded-2xl border-2 px-3 py-3.5 text-center text-sm font-semibold leading-tight shadow-sm transition active:scale-[0.99] hover:bg-slate-50 sm:text-base"
            style={{ borderColor: "#364A98", color: "#364A98" }}
            data-testid="mobile-sticky-templates-toolkits"
          >
            Templates &amp; Toolkits
          </Link>
        </div>
      </div>
      {/* Spacer so mobile content isn't hidden behind sticky CTA */}
      <div className="h-20 md:hidden" aria-hidden="true" />
    </main>
  );
}
